import React, {forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useState} from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {Card} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import {useNavigate} from "react-router-dom";
import {compose} from "redux";
import {getPackageInternalUrl} from "../../../constants/appRoutes";
import AlertDialog from "../../alert-dialog";
import CustomDialogTitle from "../../custom-dialog-title";
import CustomEmpty from "../../custom-empty";
import FullscreenDialog from "../../fullscreen-dialog";
import MaterialTable from "../../material-table";
import OdataGraphViewerButton from "../../odata-graph-viewer-button";
import OdataViewer from "../../odata-viewer";
import StatViewer from "../../stat-viewer";
import TabPanel from "../../tab-panel";
import {
  clearOtherConfigViews,
  deleteOtherConfigView,
  fetchOtherConfigViews
} from "../../../state/other-config/otherConfigActions";
import {getI18nObjCustomFilterAndSearch, localizeI18nObj} from "../../../utils/i18n";

const mapStateToProps = state => ({
  defaultLanguage: state.app.language,
  languages: state.app.languages,
  views: state.otherConfig.views,
  user: state.user
});

const mapDispatchToProps = dispatch => ({
  fetchViews: () => dispatch(fetchOtherConfigViews()),
  clearViews: () => dispatch(clearOtherConfigViews()),
  deleteView: (nodeId, id) => dispatch(deleteOtherConfigView(nodeId, id))
});

const ViewsTable = ({defaultLanguage, languages, nodes, data, onNavigate, setDeleteViewId, setDeleteViewNodeId}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [selectedView, setSelectedView] = useState(null);

  const handlePreviewOpen = view => {
    setSelectedView({
      ...view,
      layouts: view.layouts ? JSON.parse(view.layouts) : {}
    });
  };

  const handlePreviewClose = () => {
    setSelectedView(null);
  };

  return (
    <>
      <MaterialTable
        idKey={"viewsTable"}
        columns={[
          {
            field: "nodeId",
            title: t("scenes.viewsSettings.table.columns.viewNodeId"),
            render: ({nodeId}) => nodes.find(node => node.nodeId === nodeId).code,
            customFilterAndSearch: (str, {nodeId}) =>
              nodes
                .find(node => node.nodeId === nodeId)
                .code.toLowerCase()
                .includes(str.toLowerCase()),
            getClassName: () => "user-views-settings-form__view-table-node-id"
          },
          {
            field: "mode",
            render: ({mode}) =>
              mode === "ODATA_QUERY" ? (
                <AutoAwesomeIcon
                  fontSize="small"
                  sx={{marginLeft: "4px", verticalAlign: "middle", cursor: "pointer"}}
                />
              ) : null,
            renderTooltip: ({mode}) =>
              mode === "ODATA_QUERY" ? t("scenes.viewsSettings.table.columns.mode.tooltip") : "",
            width: 32
          },
          {
            field: "datasetId",
            title: t("scenes.viewsSettings.table.columns.viewDatasetId"),
            getClassName: () => "user-views-settings-form__view-table-dataset-id",
            render: ({datasetId, resourceId}) => (resourceId ? resourceId : datasetId)
          },
          {
            field: "title",
            title: t("scenes.viewsSettings.table.columns.viewTitle"),
            render: ({title}) => localizeI18nObj(title, defaultLanguage, languages),
            customFilterAndSearch: getI18nObjCustomFilterAndSearch(defaultLanguage, languages),
            getClassName: () => "user-views-settings-form__view-table-title"
          }
        ]}
        data={data}
        actions={[
          {
            className: "user-views-settings-form__view-btn",
            icon: <VisibilityIcon />,
            tooltip: t("scenes.viewsSettings.table.actions.viewView"),
            onClick: (_, view) => handlePreviewOpen(view)
          },
          ({mode}) => ({
            className: "user-views-settings-form__edit-btn",
            icon: <EditIcon />,
            tooltip: t("scenes.viewsSettings.table.actions.editView"),
            onClick: (_, {nodeId, packageId, resourceId, datasetId, viewTemplateId}) => {
              if (!window.location.href.toLowerCase().includes(`view=${viewTemplateId}`)) {
                navigate(
                  getPackageInternalUrl(
                    nodes.find(node => node.nodeId === nodeId).code,
                    [],
                    packageId,
                    resourceId || datasetId,
                    viewTemplateId
                  )
                );
              }
              if (onNavigate) onNavigate();
            },
            disabled: mode === "ODATA_QUERY"
          }),
          {
            className: "user-views-settings-form__delete-btn",
            icon: <DeleteIcon />,
            tooltip: t("scenes.viewsSettings.table.actions.deleteView"),
            onClick: (_, {nodeId, viewTemplateId}) => {
              setDeleteViewId(viewTemplateId);
              setDeleteViewNodeId(nodeId);
            }
          }
        ]}
      />
      <FullscreenDialog
        className="user-views-settings-form__preview-dialog"
        title={localizeI18nObj(selectedView?.title, defaultLanguage, languages)}
        open={selectedView}
        onClose={handlePreviewClose}
        sx={{
          "& .components__dialog__action--custom": {
            display: "none"
          }
        }}
      >
        <DialogContent className="user-views-settings-form__preview-dialog__content">
          {!selectedView ? (
            <CustomEmpty />
          ) : selectedView.mode === "ODATA_QUERY" ? (
            <Card
              className="user-views-settings-form__preview-dialog__viewer-container"
              variant="outlined"
              sx={{height: "100%"}}
            >
              <OdataViewer
                {...selectedView.layouts}
                config={{
                  ...selectedView.layouts?.config,
                  showTitle: false,
                  disableInteractions: true,
                  disableSettings: true,
                  enableAutoSecondaryDim: true,
                  rightActions: (
                    <OdataGraphViewerButton
                      baseurl={selectedView.layouts?.baseurl}
                      datasource={selectedView.layouts?.datasource}
                      mainentityset={selectedView.layouts?.mainentityset}
                      query={selectedView.layouts?.query}
                    />
                  )
                }}
              />
            </Card>
          ) : selectedView.mode === "ODATA_PARAMS" ? (
            <Card
              className="user-views-settings-form__preview-dialog__viewer-container"
              variant="outlined"
              sx={{height: "100%"}}
            >
              <OdataViewer
                {...selectedView.layouts}
                filterParams={selectedView.layouts.queryParams?.filterParams}
                groupByParams={selectedView.layouts.queryParams?.groupByParams}
                selectParams={selectedView.layouts.queryParams?.selectParams}
                config={{
                  ...selectedView.layouts?.config,
                  showTitle: false,
                  disableInteractions: true,
                  disableSettings: true
                }}
              />
            </Card>
          ) : (
            <StatViewer
              viewId={selectedView.viewTemplateId}
              packageId={selectedView.packageId}
              datasetId={selectedView.datasetId}
              nodeId={selectedView.nodeId}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose}>{t("commons.confirm.close")}</Button>
        </DialogActions>
      </FullscreenDialog>
    </>
  );
};

const UserViewsSettingsForm = (
  {defaultLanguage, languages, views, user, fetchViews, clearViews, deleteView, nodes, onNavigate},
  ref
) => {
  const [needViews, setNeedViews] = useState(true);
  const {t} = useTranslation();

  useEffect(() => {
    if (needViews) {
      setNeedViews(false);
      fetchViews();
    }
  }, [views, needViews, setNeedViews, fetchViews]);

  useImperativeHandle(ref, () => ({
    cancel(f) {
      clearViews();
      f();
    }
  }));

  const [deleteViewId, setDeleteViewId] = useState(null);
  const [deleteViewNodeId, setDeleteViewNodeId] = useState(null);

  const [tabId, setTabId] = useState("private");

  const privateViews = useMemo(() => {
    return (views || [])
      .filter(view => view.userId === user.userId)
      .filter(({nodeId}) => nodes.find(node => node.nodeId === nodeId));
  }, [views, user, nodes]);

  const sharedViews = useMemo(() => {
    return (views || [])
      .filter(view => view.userId !== user.userId)
      .filter(({nodeId}) => nodes.find(node => node.nodeId === nodeId));
  }, [views, user, nodes]);

  return (
    nodes &&
    views && (
      <Fragment>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          value={tabId}
          onChange={(_, id) => {
            setTabId(id);
          }}
          sx={{
            heigth: "49px"
          }}
        >
          <Tab
            id="user-views-settings-form__private-tab"
            value="private"
            label={t("scenes.viewsSettings.tabs.private.label")}
          />
          <Tab
            id="user-views-settings-form__shared-tab"
            value="shared"
            label={t("scenes.viewsSettings.tabs.shared.label")}
          />
        </Tabs>
        <TabPanel
          id="user-views-settings-form__tab-panel-private"
          value="private"
          selected={tabId}
          sx={{height: "calc(100% - 49px)", paddingTop: "8px"}}
        >
          <ViewsTable
            defaultLanguage={defaultLanguage}
            languages={languages}
            nodes={nodes}
            data={privateViews}
            onNavigate={onNavigate}
            setDeleteViewId={setDeleteViewId}
            setDeleteViewNodeId={setDeleteViewNodeId}
          />
        </TabPanel>
        <TabPanel
          id="user-views-settings-form__tab-panel-shared"
          value="shared"
          selected={tabId}
          sx={{height: "calc(100% - 49px)", paddingTop: "8px"}}
        >
          <ViewsTable
            defaultLanguage={defaultLanguage}
            languages={languages}
            nodes={nodes}
            data={sharedViews}
            onNavigate={onNavigate}
            setDeleteViewId={setDeleteViewId}
            setDeleteViewNodeId={setDeleteViewNodeId}
          />
        </TabPanel>

        <AlertDialog
          id="views-settings-form__modals__delete-view"
          open={deleteViewId !== null}
          onClose={() => {
            setDeleteViewId(null);
            setDeleteViewNodeId(null);
          }}
        >
          <CustomDialogTitle
            onClose={() => {
              setDeleteViewId(null);
              setDeleteViewNodeId(null);
            }}
          >
            {t("scenes.viewsSettings.modals.deleteView.title")}
          </CustomDialogTitle>
          <DialogContent>{t("scenes.viewsSettings.modals.deleteView.content")}</DialogContent>
          <DialogActions>
            <Button
              id="views-settings-form__modals__delete-view__cancel-btn"
              autoFocus
              onClick={() => {
                setDeleteViewId(null);
                setDeleteViewNodeId(null);
              }}
            >
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              id="views-settings-form__modals__delete-view__confirm-btn"
              onClick={() => {
                deleteView(deleteViewNodeId, deleteViewId);
                setDeleteViewId(null);
                setDeleteViewNodeId(null);
              }}
            >
              {t("commons.confirm.confirm")}
            </Button>
          </DialogActions>
        </AlertDialog>
      </Fragment>
    )
  );
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true}),
  forwardRef
)(UserViewsSettingsForm);
